<template>
  <v-dialog v-model="dialog" :max-width="$vuetify.breakpoint.xsOnly ? '' : '600'" :fullscreen="$vuetify.breakpoint.xsOnly" persistent>
    <v-card>
      <v-toolbar dark color="primary" flat>
        <v-toolbar-title>{{ textsMode ? $t('quotations.copy.texts_title') : $t('quotations.copy.title') }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon dark :title="$t('close')" :disabled="busy" @click="dialog = false"><v-icon>close</v-icon></v-btn>
      </v-toolbar>
      <v-card-text class="pt-5 gradient-icons">
        <template v-if="textsMode">
          <v-autocomplete
            v-model="sourceId"
            :items="sources"
            item-text="name"
            item-value="id"
            :label="$t('quotations.copy.source')"
            :loading="loadingSources"
            outlined
            dense
            hide-details
            class="mb-4"
          >
            <template v-slot:prepend><v-icon>mdi-file-document-outline</v-icon></template>
          </v-autocomplete>
          <div class="caption grey--text text-uppercase mb-1">{{ $t('quotations.copy.blocks') }}</div>
          <v-checkbox v-for="b in blocks" :key="b" v-model="selectedBlocks" :value="b" :label="$t(`quotations.texts.${b}`)" hide-details dense class="mt-1" />
        </template>
        <template v-else-if="source">
          <div class="caption grey--text mb-3">{{ $t('quotations.copy.source') }}: {{ sourceText }}</div>
          <v-autocomplete
            v-model="form.partyId"
            :items="partyItems"
            item-text="companyName"
            item-value="id"
            :label="`${$t('quotations.recipients.party')} *`"
            :loading="loadingLookups"
            outlined
            dense
            hide-details
            class="mb-4"
            @change="form.personId = null"
          >
            <template v-slot:prepend><v-icon>mdi-domain</v-icon></template>
          </v-autocomplete>
          <v-autocomplete
            v-model="form.personId"
            :items="partyPersons"
            :item-text="p => `${p.firstname} ${p.lastname}`"
            item-value="id"
            :label="$t('quotations.recipients.person')"
            :disabled="!form.partyId"
            :no-data-text="$t('quotations.recipients.no_persons')"
            outlined
            dense
            hide-details
            clearable
            class="mb-4"
          >
            <template v-slot:prepend><v-icon>mdi-account-outline</v-icon></template>
          </v-autocomplete>
          <date-picker
            :date="form.deadline"
            :label="`${$t('quotations.recipients.deadline')} *`"
            :input-styles="{ outlined: true, dense: true }"
            @change="v => (form.deadline = v)"
          />

          <div class="caption grey--text text-uppercase mt-4">{{ $t('quotations.copy.target') }}</div>
          <v-radio-group v-model="form.target" hide-details dense class="mt-1">
            <v-radio value="same" :label="$t('quotations.copy.same_project')" />
            <v-radio value="new" :label="$t('quotations.copy.new_project')" />
          </v-radio-group>
          <v-text-field
            v-if="form.target === 'new'"
            v-model="form.projectName"
            :label="`${$t('quotations.name')} *`"
            :hint="$t('quotations.copy.new_project_hint')"
            persistent-hint
            outlined
            dense
            class="mt-3"
          >
            <template v-slot:prepend><v-icon>mdi-folder-outline</v-icon></template>
          </v-text-field>

          <div class="caption grey--text text-uppercase mt-4">{{ $t('quotations.copy.parts') }}</div>
          <v-checkbox v-model="form.includeSites" :label="$t('quotations.copy.include_sites')" hide-details dense class="mt-1" />
          <v-checkbox v-model="form.includeConditional" :label="$t('quotations.copy.include_conditional')" :disabled="!form.includeSites" hide-details dense class="mt-1 ml-6" />
          <v-checkbox v-model="form.includeTexts" :label="$t('quotations.copy.include_texts')" hide-details dense class="mt-1" />
          <div class="caption grey--text mt-4">{{ $t('quotations.copy.never_copied') }}</div>
        </template>
      </v-card-text>
      <v-divider />
      <v-card-actions :class="$vuetify.breakpoint.xsOnly && 'flex-column-reverse'">
        <v-spacer />
        <v-btn :block="$vuetify.breakpoint.xsOnly" text :disabled="busy" @click="dialog = false">{{ $t('cancel') }}</v-btn>
        <v-btn
          :block="$vuetify.breakpoint.xsOnly"
          :class="$vuetify.breakpoint.xsOnly && 'mb-2'"
          color="primary"
          :loading="busy"
          :disabled="textsMode ? !sourceId || selectedBlocks.length === 0 : !copyValid"
          @click="confirm"
        >
          {{ textsMode ? $t('quotations.copy.copy_texts') : $t('quotations.copy.create') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import quotationApi from '@/api/quotation';
import DatePicker from '@/components/DatePicker';

const BLOCKS = ['intro', 'included', 'reservations', 'obligations', 'other', 'contact'];

export default {
  name: 'CopyQuotationDialog',
  components: { DatePicker },
  props: {
    // Tarjousnäkymä antaa valmiiksi ladatut; listalla ne haetaan, kun dialogi avataan ensimmäisen kerran
    parties: { type: Array, default: null },
    persons: { type: Array, default: null },
  },
  data() {
    return {
      dialog: false,
      textsMode: false,
      busy: false,
      source: null,
      form: {},
      loadedParties: [],
      loadedPersons: [],
      loadingLookups: false,
      blocks: BLOCKS,
      selectedBlocks: [...BLOCKS],
      sources: [],
      sourceId: null,
      loadingSources: false,
    };
  },
  computed: {
    partyItems() {
      return this.parties || this.loadedParties;
    },
    partyPersons() {
      return (this.persons || this.loadedPersons).filter(p => p.employer && p.employer.id === this.form.partyId);
    },
    sourceText() {
      return [this.source.name, this.source.code, this.source.partyName].filter(Boolean).join(' · ');
    },
    copyValid() {
      const f = this.form;
      return !!f.partyId && !!f.deadline && (f.target === 'same' || !!(f.projectName && f.projectName.trim()));
    },
  },
  methods: {
    // Luo kopiona: source = { id, name (projektin nimi), code, partyName, deadline }
    open(source) {
      this.textsMode = false;
      this.source = source;
      this.form = {
        partyId: null,
        personId: null,
        deadline: source.deadline || null,
        target: 'same',
        // Uusi projekti on samankaltainen kohde, joten nimi esitäytetään muokattavaksi
        projectName: source.name,
        includeSites: true,
        includeConditional: true,
        includeTexts: true,
      };
      this.dialog = true;
      if (!this.parties && this.loadedParties.length === 0) this.loadLookups();
    },
    async loadLookups() {
      this.loadingLookups = true;
      try {
        [this.loadedParties, this.loadedPersons] = await Promise.all([quotationApi.getParties(), quotationApi.getPersons()]);
      } catch (error) {
        this.$handleApiError(error);
      }
      this.loadingLookups = false;
    },
    // Kopioi tekstit toisesta tarjouksesta nykyiseen (ei tallenna)
    async openTexts(current) {
      this.textsMode = true;
      this.source = current;
      this.sourceId = null;
      this.selectedBlocks = [...BLOCKS];
      this.dialog = true;
      this.loadingSources = true;
      try {
        const page = await quotationApi.getQuotationsPaged({}, { itemsPerPage: -1, sortBy: ['updatedAt'], sortDesc: [true] });
        this.sources = (page.content || [])
          .flatMap(p => p.offers.map(o => ({ id: o.id, name: [p.name, o.code, o.partyName].filter(Boolean).join(' · ') })))
          .filter(q => q.id !== current.id);
      } catch (error) {
        this.$handleApiError(error);
      }
      this.loadingSources = false;
    },
    async confirm() {
      this.busy = true;
      try {
        if (this.textsMode) {
          const src = await quotationApi.getQuotation(this.sourceId);
          const texts = {};
          this.selectedBlocks.forEach(b => (texts[b] = (src.texts || {})[b] || ''));
          this.$emit('texts', { texts, name: src.name });
        } else {
          const f = this.form;
          const created = await quotationApi.copyQuotation(this.source.id, {
            partyId: f.partyId,
            personId: f.personId,
            deadline: f.deadline,
            includeSites: f.includeSites,
            includeConditional: f.includeSites && f.includeConditional,
            includeTexts: f.includeTexts,
            newProjectName: f.target === 'new' ? f.projectName.trim() : null,
          });
          this.$emit('copied', created);
        }
        this.dialog = false;
      } catch (error) {
        this.$handleApiError(error, error && error.message);
      }
      this.busy = false;
    },
  },
};
</script>
